import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Hash, Eye, CheckCircle } from "@phosphor-icons/react";

const fmtNums = (o) => Object.values(o || {}).join(" · ") || "—";

function Summary({ res }) {
  return (
    <div className="space-y-4" data-testid="renumber-preview">
      <div className="grid grid-cols-3 gap-3">
        {[
          ["Sales to number", res.sales, "renumber-stat-sales"],
          ["Already-numbered that change", res.previously_numbered_changed, "renumber-stat-changed"],
          ["Older / imported numbers cleared", res.out_of_range_cleared, "renumber-stat-cleared"],
        ].map(([l, v, id]) => (
          <div key={id} className="border border-canvas-line bg-canvas-panel p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{l}</div>
            <div className="text-2xl font-bold tabular-nums" data-testid={id}>{v}</div>
          </div>
        ))}
      </div>
      <table className="w-full text-sm border border-canvas-line">
        <thead className="bg-canvas-panel font-mono text-[10px] uppercase tracking-wider text-ink-muted">
          <tr><th className="text-left p-2">Branch</th><th className="text-left p-2">FY</th><th className="text-left p-2">Type</th><th className="text-right p-2">Count</th><th className="text-left p-2">Range</th></tr>
        </thead>
        <tbody>
          {res.summary.map((r, i) => (
            <tr key={i} className="border-t border-canvas-line" data-testid={`renumber-row-${i}`}>
              <td className="p-2">{r.branch}</td><td className="p-2">{r.fy}</td><td className="p-2 capitalize">{r.type.replace("_", "-")}</td>
              <td className="p-2 text-right tabular-nums">{r.count}</td><td className="p-2 font-mono text-xs">{r.first} → {r.last}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">First {res.samples.length} sales</div>
        <table className="w-full text-xs border border-canvas-line">
          <tbody>
            {res.samples.map((s, i) => (
              <tr key={i} className="border-t border-canvas-line">
                <td className="p-2">{String(s.sale_date).slice(0, 10)}</td><td className="p-2">{s.customer_name}</td>
                <td className="p-2 font-mono text-ink-muted line-through">{fmtNums(s.old) === "—" ? "" : fmtNums(s.old)}</td>
                <td className="p-2 font-mono font-bold">{fmtNums(s.new)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminInvoiceRenumberPage() {
  const [fromDate, setFromDate] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    if (!fromDate) return toast.error("Pick a start date");
    if (!dryRun && !window.confirm(`Renumber ${res.sales} sales from ${fromDate}? Old printed numbers will no longer match.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sales/admin/invoice-renumber", { from_date: fromDate, dry_run: dryRun });
      setRes(data);
      if (!dryRun) toast.success(`Renumbered ${data.sales} sales`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-5xl space-y-6" data-testid="invoice-renumber-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Hash size={26} weight="bold" /> Invoice Renumbering</h1>
        <p className="text-sm text-ink-muted mt-1">
          Gives every approved sale from the chosen date a Tally-safe invoice number (max 16 characters),
          running per branch, invoice type and financial year, in sale-date order. Migrated sales are skipped.
        </p>
      </div>
      <div className="flex items-end gap-3">
        <label className="text-xs font-mono uppercase tracking-wider text-ink-muted">
          Renumber sales from
          <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setRes(null); }}
            className="block mt-1 border border-canvas-line px-3 py-2 text-sm text-ink bg-white" data-testid="renumber-from-date" />
        </label>
        <button onClick={() => run(true)} disabled={busy} data-testid="renumber-preview-btn"
          className="px-4 py-2 border border-ink text-sm font-semibold flex items-center gap-2 hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
          <Eye size={16} /> Preview
        </button>
        {res?.dry_run && res.sales > 0 && (
          <button onClick={() => run(false)} disabled={busy} data-testid="renumber-confirm-btn"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold flex items-center gap-2 hover:bg-brand transition-colors disabled:opacity-50">
            <CheckCircle size={16} /> Confirm & Renumber
          </button>
        )}
      </div>
      {res && !res.dry_run && (
        <div className="border border-green-600 bg-green-50 text-green-800 p-3 text-sm" data-testid="renumber-done">
          Done. {res.sales} sales renumbered. New prints continue after the last number in each series.
        </div>
      )}
      {res && <Summary res={res} />}
    </div>
  );
}
